import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FileText, Lock, Volume2, Square, User, Calendar, Hash, Shield, MapPin, ClipboardList } from 'lucide-react';
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';
import { InlineResolutionField, ResolutionFieldConfig } from './InlineResolutionField';
import { AssistantResolutionPopover } from './AssistantResolutionPopover';
import { Fragment } from 'react';
import { toast } from 'sonner';

interface ReconciledNoteCardProps {
  isSigned: boolean;
  resolutionFields: ResolutionFieldConfig[];
  resolvedValues: Record<string, string>;
  onResolve: (id: string, value: string) => void;
  engineVersion?: string;
  patientName?: string;
  patientDob?: string;
  patientMrn?: string;
  patientSex?: string | null;
  surgeonName?: string;
  facilityLocation?: string;
  caseEncounterNo?: string;
  highComplexityText?: string;
  /** The AI-generated operative note text — used as the note body instead of a static template */
  optimizedNote?: string;
  /** Primary CPT code + description from the reconciliation engine */
  primaryCpt?: { code: string; description: string };
  /** Additional CPT codes from the reconciliation engine */
  additionalCpts?: { code: string; description: string }[];
  /** ICD-10 codes from the reconciliation engine */
  icd10Codes?: string[];
}

/** Markers to identify assistant-related lines for removal */
const ASSISTANT_LINE_MARKERS = [
  'Assistant Surgeon:',
  'Note: The surgical assistant was utilized',
  'CPT 09980',
];

/** Split note template text at {{field_id}} markers and render inline fields */
function renderNoteWithFields(
  template: string,
  fields: ResolutionFieldConfig[],
  values: Record<string, string>,
  onResolve: (id: string, val: string) => void,
  onRemoveAssistant: () => void,
  assistantRemoved: boolean,
) {
  // If assistant was removed, strip assistant lines from template
  let processedTemplate = template;
  if (assistantRemoved) {
    processedTemplate = template
      .split('\n')
      .filter(line => {
        const trimmed = line.trim();
        if (!trimmed) return true;
        return !ASSISTANT_LINE_MARKERS.some(marker => trimmed.includes(marker));
      })
      .join('\n');
  }

  const fieldMap = Object.fromEntries(fields.map(f => [f.id, f]));
  const parts = processedTemplate.split(/(\{\{[a-z_]+\}\})/g);

  return parts.map((part, i) => {
    const match = part.match(/^\{\{([a-z_]+)\}\}$/);
    if (match && match[1] === 'assistant_verification') {
      if (assistantRemoved) return null;
      return (
        <AssistantResolutionPopover
          key={i}
          value={values['assistant_verification'] ?? ''}
          onResolve={(val) => onResolve('assistant_verification', val)}
          onRemoveAssistant={onRemoveAssistant}
        />
      );
    }
    if (match && fieldMap[match[1]]) {
      const config = fieldMap[match[1]];
      return (
        <InlineResolutionField
          key={i}
          config={config}
          value={values[config.id] ?? ''}
          onChange={onResolve}
        />
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function buildNoteTemplate(
  patientName: string,
  patientDob: string,
  patientMrn: string,
  surgeonName: string,
  facilityLocation: string,
  caseEncounterNo: string,
  highComplexityText?: string,
  patientSex?: string | null,
  optimizedNote?: string,
  primaryCpt?: { code: string; description: string },
  additionalCpts?: { code: string; description: string }[],
  icd10Codes?: string[],
  includeAssistantSection = false,
  includeImplantSection = false,
) {
  const procedureDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const procedureTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const complexitySection = highComplexityText
    ? `\n\n{{high_complexity_bmi}}`
    : '';

  // Build dynamic procedure listing from actual CPT data
  const primaryProcedureDesc = primaryCpt?.description || 'Procedure not specified';
  const primaryProcedureCode = primaryCpt?.code || 'XXXXX';

  const procedureLines = [`1. ${primaryProcedureDesc} (CPT ${primaryProcedureCode})`];
  if (additionalCpts && additionalCpts.length > 0) {
    additionalCpts.forEach((cpt, i) => {
      procedureLines.push(`${i + 2}. ${cpt.description || cpt.code} (CPT ${cpt.code})`);
    });
  }

  const diagnosisLines = icd10Codes && icd10Codes.length > 0
    ? icd10Codes.map((code, i) => `${i + 1}. ${code}`).join('\n')
    : '1. [Diagnosis pending]';
  const assistantSection = includeAssistantSection
    ? `\nAssistant Surgeon: {{assistant_verification}} (Note: Medical necessity indicated for assistant surgeon per template.)`
    : '';
  const implantSection = includeImplantSection
    ? '\n\nThe procedure was performed as described. {{implant_verification}} were utilized as indicated.'
    : '\n\nThe procedure was performed as described.';

  const header = `Patient: ${patientName}
Date of Birth: ${patientDob}
Date of Service: ${procedureDate} ${procedureTime}

Location: ${facilityLocation}
Case/Encounter No.: ${caseEncounterNo}
Visit Type: Operative note
Provider: ${surgeonName}

${patientName}
DOB: ${patientDob}
MRN: ${patientMrn}
Date: ${procedureDate}

Purpose: Operative Note

Diagnosis:
${diagnosisLines}

Procedures Performed:
${procedureLines.join('\n')}

Surgeon: ${surgeonName}${assistantSection}`;

  // If we have an AI-generated optimized note, use it as the body
  if (optimizedNote && optimizedNote.trim().length > 0) {
    return `${header}

${optimizedNote}${complexitySection}

Estimated blood loss was {{ebl_verification}}. Total surgical time was {{time_verification}}.

${surgeonName}`;
  }

  // Fallback: generic dynamic template (no hardcoded anatomy)
  return `${header}

History
{{clinical_justification}}${complexitySection}

Operative Procedure
The patient was brought to the operating room and positioned appropriately. After induction of {{anesthesia_type}}, the surgical site was prepped and draped in standard sterile fashion.
${implantSection}

Estimated blood loss was {{ebl_verification}}. Total surgical time was {{time_verification}}.

Following completion of the procedure, the patient was transferred to recovery in stable condition.

${surgeonName}`;
}

export function ReconciledNoteCard({
  isSigned,
  resolutionFields,
  resolvedValues,
  onResolve,
  engineVersion,
  patientName = 'Patient',
  patientDob = '',
  patientMrn = '',
  patientSex,
  surgeonName = 'Surgeon',
  facilityLocation = 'Facility',
  caseEncounterNo = '',
  highComplexityText,
  optimizedNote,
  primaryCpt,
  additionalCpts,
  icd10Codes,
}: ReconciledNoteCardProps) {
  const { isSpeaking, handleSpeak } = useSpeechSynthesis();
  const [assistantRemoved, setAssistantRemoved] = useState(false);

  const handleRemoveAssistant = () => {
    setAssistantRemoved(true);
    onResolve('assistant_verification', '__REMOVED__');
    toast('Assistant removed from case', {
      description: 'Assistant Surgeon section has been removed from the operative note.',
    });
  };

  const includeAssistantSection = resolutionFields.some(field => field.id === 'assistant_verification');
  const includeImplantSection = resolutionFields.some(field => field.id === 'implant_verification');
  const noteTemplate = buildNoteTemplate(
    patientName,
    patientDob,
    patientMrn,
    surgeonName,
    facilityLocation,
    caseEncounterNo,
    highComplexityText,
    patientSex,
    optimizedNote,
    primaryCpt,
    additionalCpts,
    icd10Codes,
    includeAssistantSection,
    includeImplantSection,
  );
  const plainNote = noteTemplate.replace(/\{\{([a-z_]+)\}\}/g, (_, id) => resolvedValues[id] || '___');

  return (
    <Card
      data-clinical-print-document
      className="border-primary/30 print:w-full print:max-w-none print:shadow-none print:border-none print:p-0"
    >
      <CardContent data-clinical-print-content className="p-5 space-y-3 print:p-0">
        {/* Patient Info Header */}
        <div
          data-clinical-print-header
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-3 rounded-lg bg-muted/40 border border-border/40"
        >
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-primary" />
            <div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Patient</div>
              <div className="text-xs font-semibold text-foreground">{patientName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-primary" />
            <div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-wider">DOB</div>
              <div className="text-xs font-semibold text-foreground">{patientDob}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Hash className="w-3.5 h-3.5 text-primary" />
            <div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-wider">MRN</div>
              <div className="text-xs font-semibold text-foreground">{patientMrn}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-primary" />
            <div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Surgeon</div>
              <div className="text-xs font-semibold text-foreground">{surgeonName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            <div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Location</div>
              <div className="text-xs font-semibold text-foreground">{facilityLocation}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-3.5 h-3.5 text-primary" />
            <div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Case/Encounter No.</div>
              <div className="text-xs font-semibold text-foreground">{caseEncounterNo}</div>
            </div>
          </div>
        </div>

        <div data-clinical-print-ignore className="flex items-center justify-between print:hidden">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            Reconciled Operative Note
          </h3>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleSpeak(plainNote)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
                    aria-label={isSpeaking ? 'Stop reading' : 'Listen to note'}
                  >
                    {isSpeaking ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p className="text-xs">{isSpeaking ? 'Stop Reading' : 'Listen to Note'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isSigned && (
              <Badge className="bg-primary/10 text-primary border-primary/30 gap-1">
                <Lock className="w-3 h-3" /> Signed
              </Badge>
            )}
          </div>
        </div>

        <Separator data-clinical-print-ignore />

        <div data-clinical-print-body className="border rounded-lg bg-card p-5 space-y-3">
          <div data-clinical-print-header className="text-center border-b pb-3 space-y-0.5">
            <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Operative Note
            </p>
            <p className="text-[10px] text-muted-foreground">
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            {engineVersion && (
              <p data-clinical-print-ignore className="text-[10px] text-muted-foreground print:hidden">Engine: {engineVersion}</p>
            )}
          </div>

          <div
            data-clinical-print-section
            className="whitespace-pre-wrap text-sm leading-relaxed text-foreground min-h-[300px]"
            style={{ fontFamily: "'Times New Roman', Georgia, serif" }}
          >
            {renderNoteWithFields(noteTemplate, resolutionFields, resolvedValues, onResolve, handleRemoveAssistant, assistantRemoved)}
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground italic print:hidden">
          Note generated and reconciled against authorized CPT bundle. Click attestation buttons to confirm required compliance items.
        </p>
      </CardContent>
    </Card>
  );
}
